/**
 * SectionRoleAssigner — compact Writer/Reviewer chip + assign popover for
 * one outline section. Dark-themed to match ProposalWorkspaceLive.jsx.
 *
 * PUT/DELETE /api/pursuits/:id/proposal-architect/outline-sections/:sectionId/roles/:role
 */
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { UserPlus, X, PenLine, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_META = {
  WRITER: { label: 'Writer', icon: PenLine, color: 'text-blue-300 border-blue-500/40 bg-blue-500/10' },
  REVIEWER: { label: 'Reviewer', icon: Eye, color: 'text-amber-300 border-amber-500/40 bg-amber-500/10' },
};

export default function SectionRoleAssigner({ pursuitId, sectionId, role, assignment, onChanged }) {
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(assignment?.assignee_name || '');
  const [email, setEmail] = useState(assignment?.assignee_email || '');
  const [saving, setSaving] = useState(false);
  const meta = ROLE_META[role];

  const save = async () => {
    if (!name.trim()) {
      toast.error('Enter a name');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/outline-sections/${sectionId}/roles/${role}`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim(), email: email.trim() || null }),
        }
      );
      if (!res.ok) throw new Error();
      const saved = await res.json();
      onChanged?.(saved);
      setOpen(false);
      toast.success(`${meta.label} assigned`);
    } catch {
      toast.error(`Failed to assign ${meta.label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/outline-sections/${sectionId}/roles/${role}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error();
      onChanged?.(null);
      setOpen(false);
    } catch {
      toast.error(`Failed to clear ${meta.label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${
            assignment ? meta.color : 'text-slate-500 border-slate-700 bg-slate-800/50 hover:bg-slate-800'
          }`}
          data-testid={`section-role-${role.toLowerCase()}-${sectionId}`}
        >
          <meta.icon className="w-2.5 h-2.5" />
          {assignment ? assignment.assignee_name : `+ ${meta.label}`}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 bg-slate-900 border-slate-700 text-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-medium mb-2">{meta.label}</p>
        <div className="space-y-2">
          <Input
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-8 text-xs bg-slate-800 border-slate-700"
          />
          <Input
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-8 text-xs bg-slate-800 border-slate-700"
          />
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs flex-1" onClick={save} disabled={saving}>
              <UserPlus className="w-3 h-3 mr-1" /> Assign
            </Button>
            {assignment && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={clear} disabled={saving}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
